import { WhitelistEntry, useWhitelist } from '@/services/agent/whitelist';
import { useUserInfo } from '@/services/user/hooks';
import { useLocation } from 'react-router-dom';

/**
 * Returns true if the logged-in user's email is whitelisted for AI features.
 * Checks against the dynamic whitelist (DB-backed) for both exact email
 * matches and domain matches.
 *
 * Whitelist fetch is gated behind userInfo — only fires after we have a valid
 * authenticated session. This prevents the whitelist REST call from racing
 * with the GraphQL token refresh and triggering spurious logouts.
 */
const useIsCompanyUser = (): { isCompanyUser: boolean; isLoading: boolean } => {
  const location = useLocation();
  const isOAuthCallback = location.search.includes('code=');

  const { data: userInfo, isLoading: isLoadingUser } = useUserInfo();
  const hasUser = !!userInfo?.me?.email;
  const { data: whitelist = [], isLoading: isLoadingWhitelist } = useWhitelist(hasUser && !isOAuthCallback);

  const email = userInfo?.me?.email?.toLowerCase() ?? '';
  const domain = email.split('@')[1] ?? '';

  const isCompanyUser = whitelist.some((entry: WhitelistEntry) => {
    if (entry.entry_type === 'email') {
      return entry.value.toLowerCase() === email;
    }
    if (entry.entry_type === 'domain') {
      return entry.value.toLowerCase() === domain;
    }
    return false;
  });

  return { isCompanyUser, isLoading: isLoadingUser || isLoadingWhitelist };
};

export default useIsCompanyUser;
